import { api } from '@/lib/axios';
import { type PdfMode, renderPdf } from '@/lib/pdf';
import type { Page } from '@/services/chantiers';

export type BMStatut = 'BROUILLON' | 'VALIDE' | 'COMMANDE' | 'LIVRE';
export type BCStatut = 'BROUILLON' | 'ENVOYE' | 'CONFIRME' | 'PARTIELLEMENT_LIVRE' | 'LIVRE' | 'ANNULE';
export type ReceptionStatut = 'CONFORME' | 'NON_CONFORME';

export interface LigneBM {
  id?: string;
  article: string;
  unite?: string | null;
  quantite_demandee: number;
  quantite_commandee?: number;
  quantite_livree?: number;
}

export interface BMDTO {
  id: string;
  reference: string;
  chantier_id: string | null;
  date_demande: string | null;
  statut: BMStatut;
  notes: string | null;
  lignes: LigneBM[];
}

export interface BMInput {
  chantier_id?: string | null;
  date_demande?: string | null;
  notes?: string | null;
  lignes: { article: string; unite?: string | null; quantite_demandee: number }[];
}

/** Unités proposées pour les lignes de commande. */
export const UNITES_ACHAT = ['u', 'm', 'm2', 'm3', 'kg', 'tonne', 'sac', 'ml', 'l', 'forfait'];

export interface LigneBCDTO {
  id: string;
  designation: string;
  unite: string | null;
  quantite: number;
  prix_unitaire: number;
  montant_ligne: number;
  quantite_recue: number;
  article_stock_id: string | null;
}

export interface LigneBCInput {
  designation: string;
  unite?: string | null;
  quantite: number;
  prix_unitaire: number;
}

export interface BCDTO {
  id: string;
  reference: string | null;
  fournisseur_id: string | null;
  chantier_id: string | null;
  bm_id: string | null;
  demande_achat_id?: string | null;
  date_commande: string | null;
  date_livraison_prevue: string | null;
  date_validation: string | null;
  montant_ht: number;
  tva_pct: number;
  tva_montant: number;
  montant_ttc: number;
  conditions_paiement: string | null;
  delai_livraison: string | null;
  notes: string | null;
  statut: BCStatut;
  lignes: LigneBCDTO[];
}

export interface BCInput {
  fournisseur_id?: string | null;
  chantier_id?: string | null;
  bm_id?: string | null;
  date_commande?: string | null;
  date_livraison_prevue?: string | null;
  tva_pct: number;
  conditions_paiement?: string | null;
  delai_livraison?: string | null;
  notes?: string | null;
  lignes: LigneBCInput[];
}

export const BC_STATUTS: BCStatut[] = ['BROUILLON', 'ENVOYE', 'CONFIRME', 'PARTIELLEMENT_LIVRE', 'LIVRE', 'ANNULE'];
export const BC_STATUT_LABEL: Record<BCStatut, string> = {
  BROUILLON: 'Brouillon', ENVOYE: 'Envoyé', CONFIRME: 'Confirmé',
  PARTIELLEMENT_LIVRE: 'Reçu partiel', LIVRE: 'Reçu total', ANNULE: 'Annulé',
};

export async function listBM(): Promise<Page<BMDTO>> {
  const { data } = await api.get<Page<BMDTO>>('/bons-materiaux');
  return data;
}
export async function createBM(payload: BMInput): Promise<BMDTO> {
  const { data } = await api.post<BMDTO>('/bons-materiaux', payload);
  return data;
}

/* --------------------------- Demandes d'achat (DA) ------------------------ */
export type DAStatut = 'BROUILLON' | 'VALIDEE' | 'CONVERTIE' | 'REFUSEE';
export const DA_STATUT_LABEL: Record<DAStatut, string> = {
  BROUILLON: 'Brouillon', VALIDEE: 'Validée', CONVERTIE: 'Convertie', REFUSEE: 'Refusée',
};
export type AxeAnalytique = 'CHANTIER' | 'CENTRE_COUT';
export interface LigneDA { id?: string; designation: string; unite?: string | null; quantite: number; prix_unitaire?: number; montant_ligne?: number; article_stock_id?: string | null }
export interface DADTO {
  id: string;
  reference: string | null;
  date_demande: string | null;
  statut: DAStatut;
  notes: string | null;
  motif_refus: string | null;
  demandeur_id: string | null;
  demandeur_nom: string | null;
  axe_analytique: AxeAnalytique | null;
  chantier_id: string | null;
  centre_cout_id: string | null;
  fournisseur_id: string | null;
  fournisseur_nom_libre: string | null;
  fournisseur_nom: string | null;
  comptabilise: boolean;
  tva_deductible: boolean;
  tva_pct: number;
  montant_ht: number;
  tva_montant: number;
  montant_ttc: number;
  bc_id: string | null;
  date_validation: string | null;
  lignes: LigneDA[];
}
export interface DAInput {
  date_demande?: string | null;
  notes?: string | null;
  demandeur_id?: string | null;
  axe_analytique?: AxeAnalytique | null;
  chantier_id?: string | null;
  centre_cout_id?: string | null;
  fournisseur_id?: string | null;
  fournisseur_nom_libre?: string | null;
  comptabilise?: boolean;
  tva_deductible?: boolean;
  tva_pct?: number;
  lignes: { designation: string; unite?: string | null; quantite: number; prix_unitaire?: number; article_stock_id?: string | null }[];
}

export async function listDA(): Promise<Page<DADTO>> {
  const { data } = await api.get<Page<DADTO>>('/demandes-achat');
  return data;
}
export async function createDA(payload: DAInput): Promise<DADTO> {
  const { data } = await api.post<DADTO>('/demandes-achat', payload);
  return data;
}
export async function updateDA(id: string, payload: Partial<DAInput>): Promise<DADTO> {
  const { data } = await api.put<DADTO>(`/demandes-achat/${id}`, payload);
  return data;
}
/** Valide la DA : attribue le n° définitif (DA-AAAA-NNNN). */
export async function validerDA(id: string): Promise<DADTO> {
  const { data } = await api.post<DADTO>(`/demandes-achat/${id}/valider`);
  return data;
}
export async function refuserDA(id: string, motif_refus?: string | null): Promise<DADTO> {
  const { data } = await api.post<DADTO>(`/demandes-achat/${id}/refuser`, { motif_refus: motif_refus ?? null });
  return data;
}
/** Convertit la DA validée en bon de commande brouillon pré-rempli. */
export async function convertirDA(id: string): Promise<BCDTO> {
  const { data } = await api.post<BCDTO>(`/demandes-achat/${id}/convertir`);
  return data;
}
export async function deleteDA(id: string): Promise<void> {
  await api.delete(`/demandes-achat/${id}`);
}

/* ------------------------------ Bons de commande -------------------------- */
export async function listBC(): Promise<Page<BCDTO>> {
  const { data } = await api.get<Page<BCDTO>>('/bons-commande');
  return data;
}
export async function getBC(id: string): Promise<BCDTO> {
  const { data } = await api.get<BCDTO>(`/bons-commande/${id}`);
  return data;
}
export async function createBC(payload: BCInput): Promise<BCDTO> {
  const { data } = await api.post<BCDTO>('/bons-commande', payload);
  return data;
}
export async function updateBC(id: string, payload: Partial<BCInput>): Promise<BCDTO> {
  const { data } = await api.put<BCDTO>(`/bons-commande/${id}`, payload);
  return data;
}
/** Valide le BC : attribue le n° définitif (BC-AAAA-NNNN) et passe au statut confirmé. */
export async function validerBC(id: string): Promise<BCDTO> {
  const { data } = await api.post<BCDTO>(`/bons-commande/${id}/valider`);
  return data;
}
export async function changerStatutBC(id: string, statut: BCStatut): Promise<BCDTO> {
  const { data } = await api.post<BCDTO>(`/bons-commande/${id}/statut`, null, { params: { statut } });
  return data;
}
/** Ouvre/télécharge le bon de commande imprimable (PDF). */
export async function openBCPdf(id: string, mode: PdfMode = 'open'): Promise<void> {
  await renderPdf(`/bons-commande/${id}/pdf`, undefined, mode);
}

export async function openDAPdf(id: string, mode: PdfMode = 'open'): Promise<void> {
  await renderPdf(`/demandes-achat/${id}/pdf`, undefined, mode);
}

export async function openReceptionPdf(id: string, mode: PdfMode = 'open'): Promise<void> {
  await renderPdf(`/receptions/${id}/pdf`, undefined, mode);
}

/** Ouvre/télécharge la fiche d'enregistrement de la facture (PDF). */
export async function openFacturePdf(id: string, mode: PdfMode = 'open'): Promise<void> {
  await renderPdf(`/factures/${id}/pdf`, undefined, mode);
}

export interface AchatsSynthese { engage_total: number; facture_total: number; a_receptionner: number; a_enregistrer: number }
export async function getAchatsSynthese(): Promise<AchatsSynthese> {
  const { data } = await api.get<AchatsSynthese>('/achats/synthese');
  return data;
}

/* -------------------------------- Réceptions ------------------------------ */
export interface LigneReceptionDTO { id: string; article: string; unite: string | null; quantite_commandee: number; quantite_recue: number; prix_unitaire: number; montant: number }
export interface ReceptionDTO {
  id: string;
  reference: string | null;
  bc_id: string;
  bc_reference: string | null;
  fournisseur_nom: string | null;
  chantier_id: string | null;
  depot_id: string | null;
  date_reception: string | null;
  reference_bl_fournisseur: string | null;
  statut: ReceptionStatut;
  notes: string | null;
  montant_recu: number;
  lignes: LigneReceptionDTO[];
}

export async function listReceptions(): Promise<Page<ReceptionDTO>> {
  const { data } = await api.get<Page<ReceptionDTO>>('/receptions');
  return data;
}
export async function createReception(payload: {
  bc_id: string;
  depot_id?: string | null;
  date_reception?: string;
  reference_bl_fournisseur?: string;
  statut: ReceptionStatut;
  notes?: string;
  lignes: { bc_ligne_id: string; quantite_recue: number }[];
}): Promise<unknown> {
  const { data } = await api.post('/receptions', payload);
  return data;
}

/* --------------------------- Factures fournisseur ------------------------- */
export type FactureStatut = 'BROUILLON' | 'VALIDEE' | 'PAYEE' | 'LITIGE' | 'COMPTABILISEE';
export const FACTURE_STATUT_LABEL: Record<FactureStatut, string> = {
  BROUILLON: 'Reçue', VALIDEE: 'À payer', PAYEE: 'Payée', LITIGE: 'Litige', COMPTABILISEE: 'Comptabilisée',
};
/** Statuts proposés dans le registre comptable (cycle Reçue → À payer → Payée → Comptabilisée). */
export const FACTURE_STATUTS: FactureStatut[] = ['BROUILLON', 'VALIDEE', 'PAYEE', 'COMPTABILISEE', 'LITIGE'];

export type TypeDocFacture = 'FACTURE' | 'SITUATION' | 'AVOIR';
export const TYPE_DOC_LABEL: Record<TypeDocFacture, string> = { FACTURE: 'Facture', SITUATION: 'Situation de travaux', AVOIR: 'Avoir' };

/** Statut de paiement dérivé des paiements (G50 à l'encaissement). */
export type StatutPaiement = 'NON_PAYEE' | 'PARTIELLE' | 'SOLDEE';
export const STATUT_PAIEMENT_LABEL: Record<StatutPaiement, string> = {
  NON_PAYEE: 'Non payée', PARTIELLE: 'Partielle', SOLDEE: 'Soldée',
};

export interface PaiementDTO {
  id: string;
  facture_id: string;
  date_paiement: string;
  periode_mois: number;
  periode_annee: number;
  montant_paye: number;
  ht_paiement: number;
  tva_paiement: number;
  mode_paiement: ModePaiementFacture | null;
  paiement_reference: string | null;
  paiement_banque: string | null;
  paiement_echeance: string | null;
  justificatif_url: string | null;
}
export interface PaiementInput {
  date_paiement: string;
  montant_paye: number;
  periode_mois?: number | null;
  periode_annee?: number | null;
  mode_paiement?: ModePaiementFacture | null;
  paiement_reference?: string | null;
  paiement_banque?: string | null;
  paiement_echeance?: string | null;
  justificatif_url?: string | null;
}

export type ModePaiementFacture = 'ESPECES' | 'CHEQUE' | 'VIREMENT' | 'EFFET' | 'NON_PAYE';
export const MODES_PAIEMENT_FACTURE: ModePaiementFacture[] = ['ESPECES', 'CHEQUE', 'VIREMENT', 'EFFET', 'NON_PAYE'];
export const MODE_PAIEMENT_FACTURE_LABEL: Record<ModePaiementFacture, string> = {
  ESPECES: 'Espèces', CHEQUE: 'Chèque', VIREMENT: 'Virement', EFFET: 'Effet', NON_PAYE: 'Non payé (crédit)',
};
/** Taux de TVA proposés (BTPH : 19 % dominant) ; « Exonéré » distinct du 0 %. */
export const TVA_TAUX_FACTURE: { value: string; label: string; pct: number; exonere: boolean }[] = [
  { value: '0.19', label: '19 %', pct: 0.19, exonere: false },
  { value: '0.09', label: '9 %', pct: 0.09, exonere: false },
  { value: '0.07', label: '7 %', pct: 0.07, exonere: false },
  { value: '0', label: '0 %', pct: 0, exonere: false },
  { value: 'EX', label: 'Exonéré', pct: 0, exonere: true },
];

export interface FactureTotaux {
  nombre: number;
  total_ht: number;
  total_tva: number;
  total_ttc: number;
  total_paye_periode: number;
}

/** Type de tiers d'une facture d'achat : fournisseur ou sous-traitant. */
export type TiersTypeFacture = 'FOURNISSEUR' | 'SOUS_TRAITANT';
export const TIERS_TYPES_FACTURE: TiersTypeFacture[] = ['FOURNISSEUR', 'SOUS_TRAITANT'];
export const TIERS_TYPE_FACTURE_LABEL: Record<TiersTypeFacture, string> = {
  FOURNISSEUR: 'Fournisseur', SOUS_TRAITANT: 'Sous-traitant',
};

/** Pièce jointe d'une facture (scan, photo, justificatif de paiement). */
export interface PieceJointe {
  url: string;
  nom?: string | null;
}

export interface FactureFiltres {
  mois?: number | null;
  annee?: number | null;
  mois_paiement?: number | null;
  type_doc?: TypeDocFacture | null;
  comptabilisee?: boolean | null;
  tiers_type?: TiersTypeFacture | null;
  fournisseur_id?: string | null;
  statut?: FactureStatut | null;
  chantier_id?: string | null;
  mode_paiement?: ModePaiementFacture | null;
  tva_pct?: number | null;
}

export interface FactureDTO {
  id: string;
  reference: string | null;
  type_doc: TypeDocFacture;
  comptabilisee: boolean;
  numero_facture: string | null;
  tiers_type: TiersTypeFacture | null;
  fournisseur_id: string | null;
  bc_id: string | null;
  chantier_id: string | null;
  date_facture: string | null;
  date_echeance: string | null;
  montant_ht: number;
  tva_pct: number;
  tva_exonere: boolean;
  tva_montant: number;
  montant_ttc: number;
  mode_paiement: ModePaiementFacture | null;
  paiement_reference: string | null;
  paiement_banque: string | null;
  paiement_date: string | null;
  paiement_echeance: string | null;
  justificatif_url: string | null;
  pieces: PieceJointe[];
  statut: FactureStatut;
  date_validation: string | null;
  date_paiement: string | null;
  notes: string | null;
  paiements: PaiementDTO[];
  total_paye: number;
  reste_a_payer: number;
  statut_paiement: StatutPaiement;
}

export interface FactureInput {
  type_doc?: TypeDocFacture;
  comptabilisee?: boolean;
  numero_facture?: string | null;
  tiers_type?: TiersTypeFacture | null;
  fournisseur_id?: string | null;
  bc_id?: string | null;
  chantier_id?: string | null;
  date_facture?: string | null;
  date_echeance?: string | null;
  montant_ht: number;
  tva_pct: number;
  tva_exonere?: boolean;
  tva_montant?: number | null;
  montant_ttc?: number | null;
  mode_paiement?: ModePaiementFacture | null;
  paiement_reference?: string | null;
  paiement_banque?: string | null;
  paiement_date?: string | null;
  paiement_echeance?: string | null;
  statut?: FactureStatut | null;
  date_paiement?: string | null;
  justificatif_url?: string | null;
  pieces?: PieceJointe[];
  notes?: string | null;
}

export interface RapprochementDTO {
  bc_reference: string | null;
  commande_ttc: number;
  recu_ttc: number;
  facture_ttc: number;
  ecart_commande: number;
  ecart_recu: number;
  qte_commandee: number;
  qte_recue: number;
  conforme: boolean;
}

export async function listFactures(filtres?: FactureFiltres): Promise<Page<FactureDTO>> {
  const params = { size: 200, ...filtres };
  const { data } = await api.get<Page<FactureDTO>>('/factures', { params });
  return data;
}
export async function getFacturesTotaux(filtres?: FactureFiltres): Promise<FactureTotaux> {
  const { data } = await api.get<FactureTotaux>('/factures/totaux', { params: filtres ?? {} });
  return data;
}
export async function updateFacture(id: string, payload: Partial<FactureInput>): Promise<FactureDTO> {
  const { data } = await api.put<FactureDTO>(`/factures/${id}`, payload);
  return data;
}
export async function addPaiement(factureId: string, payload: PaiementInput): Promise<FactureDTO> {
  const { data } = await api.post<FactureDTO>(`/factures/${factureId}/paiements`, payload);
  return data;
}
export async function updatePaiement(paiementId: string, payload: PaiementInput): Promise<FactureDTO> {
  const { data } = await api.put<FactureDTO>(`/paiements/${paiementId}`, payload);
  return data;
}
export async function deletePaiement(paiementId: string): Promise<void> {
  await api.delete(`/paiements/${paiementId}`);
}
export async function createFacture(payload: FactureInput): Promise<FactureDTO> {
  const { data } = await api.post<FactureDTO>('/factures', payload);
  return data;
}
export async function getRapprochement(id: string): Promise<RapprochementDTO> {
  const { data } = await api.get<RapprochementDTO>(`/factures/${id}/rapprochement`);
  return data;
}
export async function validerFacture(id: string): Promise<FactureDTO> {
  const { data } = await api.post<FactureDTO>(`/factures/${id}/valider`);
  return data;
}
export async function deleteFacture(id: string): Promise<void> {
  await api.delete(`/factures/${id}`);
}

/**
 * Quote-part HT / TVA d'un règlement de montant TTC, au taux de la facture.
 * Fonction UNIQUE partagée par l'aperçu (achat & vente), alignée sur le calcul serveur :
 *   HT = M / (1 + t) ; TVA = M − HT ; (exonéré ou t=0 ⇒ HT = M, TVA = 0).
 * Le taux est coercé en nombre : l'API sérialise les décimaux en CHAÎNE ("0.19"), or
 * `1 + "0.19"` concatène en "10.19" au lieu d'additionner — d'où la répartition cassée.
 */
export function quotePartHtTva(
  montantTtc: number, tvaPct: number | string, exonere?: boolean,
): { ht: number; tva: number } {
  const t = exonere ? 0 : Number(tvaPct) || 0;
  const ht = t > 0 ? Math.round((montantTtc / (1 + t)) * 100) / 100 : montantTtc;
  const tva = Math.round((montantTtc - ht) * 100) / 100;
  return { ht, tva };
}
